<template>
  <modal v-bind="$attrs" v-on="$listeners" :center="true" width="848px">
    <template v-slot:title>
      <h1 style="text-align: center">{{ $t("read_aloud.modals.addAssignment.add_assignment") }}</h1>
    </template>
    <template v-slot:body v-loading="primaryLoading">
      <div>
        <div class="section">
          <h6 class="section-header">{{ $t("read_aloud.modals.addAssignment.assignment_details") }}</h6>
          <div class="section-container">
            <assignment-study-set ref="assignmentDetails" />
          </div>
          <el-form v-if="assessments.length > 0" class="assignment-name-from">
            <el-row :gutter="24" class="form-row">
              <el-col :md="24" :xs="24">
                <elsa-input v-model="assessments[0].name" :disabled="true" name="name" class="assessment-name" />
              </el-col>
            </el-row>
          </el-form>
        </div>
        <div class="section">
          <h6 class="section-header" style="margin-bottom: 24px;">
            {{ $t("read_aloud.modals.addAssignment.other_settings") }}
          </h6>
          <div class="section-container">
            <el-row type="flex" style="width: 100%" justify="center">
              <el-col :span="23">
                <ul class="assigned-classes-list">
                  <li v-for="(item, index) in assignments" :key="index">
                    <assigned-class-setting-detail :index="index" ref="assignedClassSettingDetail" />
                    <hr style="margin: 32px 0px" v-if="index < assignments.length - 1" />
                  </li>
                </ul>
                <el-button
                  type="text"
                  class="invite-teacher"
                  @click="handleAssignToAnotherClass"
                  style="margin-bottom: 28px; margin-top: -16px"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" class="pr-px-8" />
                  {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_another_group`)) }}
                </el-button>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <tertiary-button class="confirm-cancel" @click.native="handleLeave">
        {{ $t("common.cancel") }}
      </tertiary-button>
      <primary-button
        class="confirm-delete"
        v-loading="primaryLoading"
        ref=""
        @click.native="handleCreate"
        :disabled="!primaryButtonEnabled"
      >
        {{ $t("common.assign") }}
      </primary-button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/common/Modal";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import AssignedClassSettingDetail from "./add-assignments/AssignedClassSettingDetail";
import { mapActions, mapGetters } from "vuex";
import { pathOr } from "ramda";
import AssignmentStudySet from "./add-assignments/AssignmentStudySet.vue";
import ElsaInput from "@/components/common/Input";

export default {
  components: {
    Modal,
    PrimaryButton,
    TertiaryButton,
    AssignedClassSettingDetail,
    AssignmentStudySet,
    ElsaInput,
  },
  data() {
    return {
      primaryLoading: false,
      showDetailStudySets: [],
      searchableClasses: [],
      searchableStudySets: [],
      emptyCheckStatus: 0,
    };
  },
  computed: {
    ...mapGetters({
      buildingAssignments: "readAlouds/getBuildingAssignments",
      currentOrganizationType: "organization/currentOrganizationType",
    }),
    assignments() {
      return pathOr([], ["buildingAssignments", "assignments"])(this);
    },
    assignedStudySetIds() {
      if (this.assignedStudySets.length > 0) {
        return this.assignedStudySets.map(x => x.id);
      }
      return []
    },
    assignedReadAloudIds() {
      if (this.assignedReadAloud) {
        return [this.assignedReadAloud.id];
      }
      return []
    },
    assignedReadAloud() {
      return pathOr([], ["buildingAssignments", "readAloud"])(this);
    },
    assignedStudySets() {
      return pathOr([], ["buildingAssignments", "studySets"])(this);
    },
    assessments() {
      return pathOr([], ["buildingAssignments", "assessments"])(this);
    },
    getTitle() {
      return this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "modals.delete.title"), {
        numberOfClasses: this.numberOfSelectedClasses,
      });
    },
    primaryButtonEnabled() {
      return (
        this.assignments.length > 0 &&
        this.assignments.every(x => {
          return x.name && x.groupIds && x.groupIds.length && x.startDate != null && x.startTime && x.dueDate && x.dueTime;
        })
      );
    },
  },
  methods: {
    ...mapActions({
      createAssignments: "readAlouds/createAssignments",
      updateBuildingAssignments: "readAlouds/updateBuildingAssignments",
      resetAssignments: "readAlouds/resetBuildingAssignments",
      createAssignmentLibrary: "homeworks/createAssignmentLibrary",
    }),
    handleLeave() {
      this.$emit("closed");
    },
    async handleCreate() {
      try {
        this.primaryLoading = true;
        // Create assignment library first
        let assignmentLibrary = {
          name: this.buildingAssignments.assignments[0].name,
          lesson_original_ids: [],
          read_aloud: this.assignedReadAloudIds,
          study_sets: this.assignedStudySetIds,
          assessment_ids: this.assessments.map(x => x.id),
          shareable: this.buildingAssignments.assignments[0].shareable,
        };
        let assignment_library_id = await this.createAssignmentLibrary(assignmentLibrary);
        await this.createAssignments(assignment_library_id);

        this.primaryLoading = false;
        this.review = true;
        this.$emit("done");
      } catch (error) {
        this.errors = error;
        this.primaryLoading = false;
      }
    },
    reset() {
      this.resetAssignments();
    },
    setInitialReadAloud(data) {
      const studySets = data['warm_up_ss'] ? [data['warm_up_ss']] : []
      this.updateBuildingAssignments({
        readAloud: data,
        studySets: studySets,
        assignments: [
          {
            reminderBeforeDay: 5,
          },
        ],
      });
    },
    setInitialAssessment(assessment) {
      this.updateBuildingAssignments({
        assessments: [assessment],
        assignments: [
          {
            reminderBeforeDay: 5,
          },
        ],
      });
    },
    handleAssignToAnotherClass() {
      const assignments = this.assignments;
      assignments.push({
        reminderBeforeDay: 5,
      });
      this.updateBuildingAssignments({
        assignments: assignments,
      });
    },
    emptyCheck() {
      this.$refs.assignedClassSettingDetail.every(x => {
        if (x.assignment.name || x.assignment.groupIds || x.assignment.startDate || x.assignment.dueDate)
          this.emptyCheckStatus = 1;
      });
      return this.emptyCheckStatus;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/_colors.scss";

#leave-modal-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 32px;
  text-align: center;
  color: $black;
  margin: 0 auto;
}

#leave-modal-desc {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: $black;
  margin: 4px auto;
}

.elsa-button.confirm-delete,
.elsa-button.confirm-delete:hover {
  border-radius: 8px;
}

.elsa-button.confirm-cancel,
.elsa-button.confirm-cancel:hover {
  border-radius: 8px;
  padding-left: 16px;
  padding-right: 16px;
}

ul {
  list-style-type: none;
  padding-left: 0px;
  margin-bottom: 0px;
  margin-top: 0px;
}

.section-container {
  border: 1px solid #d8d8d8;
  box-sizing: border-box;
  border-radius: 8px;
}
.assigned-classes-list {
  padding-top: 38px;
  li {
    box-shadow: inset 0px -1px 0px #e6e6e6;
  }

  > :last-child {
    margin-bottom: 0px;
    box-shadow: none;
  }
}

.section-header {
  font-size: 20px;
  line-height: 30px;
  color: $black;
  font-weight: 700;
  margin-top: 0px;
  margin-bottom: 8px;
  letter-spacing: 0 !important;
}

::v-deep {
  .el-loading-mask {
    border-radius: 8px !important;
  }
  .el-form .el-form-item {
    margin-bottom: 24px;
  }

  .el-form .el-form-item .el-form-item__label {
    margin-bottom: 8px;
    color: $black;
    font-weight: 600;
    line-height: 24px;
  }

  .el-button--text {
    color: $hyperlink;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  .el-dialog.elsa-modal {
    border-radius: 8px;
    .el-dialog__header,
    .el-dialog__body {
      //padding: 40px 40px 0 40px;
      // display: flex;
      background-color: white;
    }

    &.signup .el-dialog__body {
      padding: 0;
    }

    .el-dialog__header {
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
      min-height: 27.5px;
      padding: 40px 24px 10px;
    }

    .el-dialog__body {
      border-top: none;
      & > *:not(p):not(.info) {
        padding-bottom: 6px;
        display: flex;
        flex-direction: column;
      }
    }

    .el-dialog__footer {
      padding: 0 20px 48px 20px;
      justify-content: center;
    }
  }
}
.assignment-name-from {
  padding-bottom: 0px;
  .el-form-item {
    margin-bottom: 12px !important;
  }
}
h1 {
  font-weight: 700;
  font-size: 30px;
  line-height: 45px;
  letter-spacing: 0em;
}

::v-deep .el-dialog.elsa-modal .el-dialog__body {
  opacity: 1;
  padding: 30px 80px;
}

::v-deep .el-input__prefix,
.el-input__suffix {
  top: 4px;
}
</style>
